import { inject, Injectable } from '@angular/core';
import { map, Observable, of, tap } from 'rxjs';
import { InputsService } from '@app/inputs/services/inputs.service';
import { InputImageUploadAdapterService } from '@app/uploader/upload-section/adapters/input-image-upload-adapter.service';
import { InputsFacadeBaseV2 } from '@app/user-content/user-input-v2/services/inputs-facade-base';
import { AuthService } from '@dg/shared-services';
import { APIParameterModel, ViewModel } from '../event.model';
import { EventMapper } from './event-mapper.service';
import { InputContext } from '@app/user-content/user-input/user-input.model';
import { SubmissionStatus } from '@app/inputs/inputs.model';
import { getModalHeaderText } from '../event.utils';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { EventService } from './event.service';
import { markFormAsTouched } from '@app/shared/utils/angular-form-field-helpers';
import { FormGroup } from '@angular/forms';
import { EventTrackerService } from '../event-tracker.service';

@Injectable()
export abstract class EventBaseFacade extends InputsFacadeBaseV2<ViewModel> {
  public mapperService = inject(EventMapper);
  public authService = inject(AuthService);
  public activeModal = inject(NgbActiveModal);
  public router = inject(Router);
  public eventService = inject(EventService);
  public trackerService = inject(EventTrackerService);

  public get orgName() {
    return this.authService.authUser.defaultOrgInfo?.name;
  }

  public get orgId() {
    return this.authService.authUser.defaultOrgInfo?.organizationId;
  }

  constructor(
    protected override inputsService: InputsService,
    protected override inputImageUploadAdapterService: InputImageUploadAdapterService
  ) {
    super(inputsService, inputImageUploadAdapterService);
  }

  public onSubmit(form: FormGroup) {
    // Validate form
    markFormAsTouched(form);

    if (!form.valid) {
      return of(null);
    }

    const parameters = this.mapperService.buildApiParameters(
      this.viewModel,
      form.value
    );

    const saveAction$ = (
      this.viewModel.inputContext.isEditing
        ? this.updateEvent.bind(this)
        : this.addEvent.bind(this)
    )(parameters);

    return super
      .manageSubmitStatus(saveAction$)
      .pipe(tap(() => this.performSuccessSideEffects()));
  }

  public initializeViewModel(
    inputContext: InputContext
  ): Observable<ViewModel> {
    // Listen for changes on submission Status
    const shouldSpinSubmitButton$ = this.submissionStatus$.pipe(
      map((status) => status === SubmissionStatus.Submitting)
    );

    // initialize context
    super.initializeContextViewModel(inputContext);

    // Base i18n strings shared across child facades
    const i18n = this.translateService.instant([
      'Core_Next',
      'Core_Title',
      'Core_Description',
      'Core_Skills',
      'EventFormCtrl_AddEvent',
      'EventFormCtrl_EditEvent',
      'EventFormCtrl_ValidUrl',
      'EventFormCtrl_SaveEvent',
      'EventFormCtrl_EventWebsite',
      'MediaFormCtrl_TitleRequired',
      'dgFlexRow_EditSectionTitlePlaceholder',
      'dgOrgInternalContentForm_EventDescriptionPlaceholder',
      'dgOrgInternalContentForm_ContentOwner',
      'dgOrgInternalContentForm_ContentOwnerTooltip',
      'dgOrgInternalContentForm_ContentOwnerPlaceholder',
      'MediaFormCtrl_ValidContentOwnerRequired',
      'dgOrgInternalContentForm_ContentOwnerRequired',
      'dgOrgInternalContent_SkillsTooltipText',
      'dgOrgInternalContent_SkillsPlaceholderText',
      'dgOrgInternalContent_SkillsMaxError',
    ]);

    const modalHeader = getModalHeaderText(i18n, inputContext.isEditing);

    return super.patchViewModel({
      i18n,
      modalHeader,
      shouldSpinSubmitButton$,
      inputType: 'Event',
      onNext: this.onNext.bind(this),
      onSubmit: this.onSubmit.bind(this),
    });
  }

  public performSuccessSideEffects() {
    this.activeModal.close(this.viewModel);
  }

  private addEvent(parameters: APIParameterModel) {
    return this.eventService
      .addEvent(parameters)
      .pipe(
        tap(() =>
          this.trackerService.trackContentCatalogAdd(this.viewModel, parameters)
        )
      );
  }

  private updateEvent(parameters: APIParameterModel) {
    return this.eventService
      .updateEvent(parameters)
      .pipe(
        tap(() => this.trackerService.trackContentCatalogUpdate(parameters))
      );
  }
}
